import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    IconButton,
    Modal,
    Paper,
    Slide,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Theme,
    Tooltip,
    Typography,
    useTheme,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import EventIcon from '@material-ui/icons/Event'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import AddIcon from '@material-ui/icons/Add'
import GetAppIcon from '@material-ui/icons/GetApp'
import PublishIcon from '@material-ui/icons/Publish'
import { useUser } from '../../../../hooks'
import type { CreateBulkDeadlinesRequest } from '../../../../hooks'
import { IdBoolMap } from '../../../../models'
import { toast } from 'react-toastify'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { DateTime } from 'luxon'
import { APARTMENT } from '../../../../helpers'

export interface BulkDeadlineRow {
    id: string
    date: string
    title: string
    description: string
    reminderDate: string
}

const CSV_HEADER = 'date,title,description,reminder_date'
const EXAMPLE_CSV = `${CSV_HEADER}
2025-04-15,Lease renewal,Annual lease renewal for all units,2025-04-01
2025-05-01,Fire inspection,Schedule fire safety inspection,2025-04-15
2025-06-15,Insurance renewal,Property insurance policy renewal,`

/** Try common date formats and return YYYY-MM-DD or empty string. */
function normalizeDateToISO(dateStr: string): string {
    const s = (dateStr || '').trim()
    if (!s) return ''
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
    const formats = [
        'yyyy-MM-dd',
        'MM/dd/yyyy',
        'M/d/yyyy',
        'dd/MM/yyyy',
        'd/M/yyyy',
        'MM-dd-yyyy',
        'M-d-yyyy',
        'dd-MM-yyyy',
        'd-M-yyyy',
        'yyyy/MM/dd',
    ]
    for (const fmt of formats) {
        const parsed = DateTime.fromFormat(s, fmt)
        if (parsed.isValid) return parsed.toISODate() ?? ''
    }
    // Last resort: try ISO with time or fromString
    const fallback = DateTime.fromISO(s)
    if (fallback.isValid) return fallback.toISODate() ?? ''
    return ''
}

function parseCSVLine(line: string): string[] {
    const result: string[] = []
    let current = ''
    let inQuotes = false
    for (let i = 0; i < line.length; i++) {
        const c = line[i]
        if (c === '"') {
            inQuotes = !inQuotes
        } else if ((c === ',' && !inQuotes) || (c === '\n' && !inQuotes)) {
            result.push(current.trim())
            current = ''
            if (c === '\n') break
        } else {
            current += c
        }
    }
    result.push(current.trim())
    return result
}

function parseCSV(csvText: string): BulkDeadlineRow[] {
    const lines = csvText.split(/\r?\n/).filter((l) => l.trim())
    if (lines.length < 2) return []
    const header = parseCSVLine(lines[0]).map((h) => h.toLowerCase().trim())
    const dateIdx = header.findIndex((h) => h === 'date')
    const titleIdx = header.findIndex((h) => h === 'title')
    const descIdx = header.findIndex((h) => h === 'description')
    const reminderIdx = header.findIndex((h) => h === 'reminder_date')
    if (dateIdx === -1 || titleIdx === -1 || descIdx === -1) return []
    const rows: BulkDeadlineRow[] = []
    for (let i = 1; i < lines.length; i++) {
        const cells = parseCSVLine(lines[i])
        const date = (cells[dateIdx] ?? '').trim()
        const title = (cells[titleIdx] ?? '').trim()
        const description = (cells[descIdx] ?? '').trim()
        const reminderRaw =
            reminderIdx >= 0 ? (cells[reminderIdx] ?? '').trim() : ''
        if (!date && !title && !description) continue
        rows.push({
            id: `row-${i}-${Date.now()}`,
            date: normalizeDateToISO(date) || '',
            title: title || '',
            description: description || '',
            reminderDate: reminderRaw
                ? normalizeDateToISO(reminderRaw) || ''
                : '',
        })
    }
    return rows
}

const useStyles = makeStyles((theme: Theme) => ({
    modalBackdrop: {
        display: 'flex',
    },
    paper: {
        display: 'flex',
        flexDirection: 'column',
        outline: 'none',
        borderRadius: 12,
        boxShadow: theme.shadows[10],
        height: '85vh',
        maxHeight: '90vh',
        minHeight: 400,
        width: '96vw',
        maxWidth: 1400,
        minWidth: 640,
        overflow: 'hidden',
    },
    header: {
        padding: theme.spacing(2.5, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        backgroundColor: theme.palette.background.paper,
    },
    headerIcon: {
        color: theme.palette.primary.main,
        fontSize: 28,
    },
    headerTitle: {
        fontWeight: 600,
        fontSize: '1.25rem',
    },
    body: {
        padding: theme.spacing(3),
        overflowY: 'auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
    },
    sectionLabel: {
        marginBottom: theme.spacing(0.5),
        fontWeight: 600,
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
    },
    csvActions: {
        display: 'flex',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
    },
    tableWrap: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        overflowX: 'auto',
        overflowY: 'visible',
        backgroundColor: theme.palette.background.default,
    },
    table: {
        minWidth: 720,
    },
    dateCell: {
        minWidth: 200,
        width: 200,
        overflow: 'visible',
        boxSizing: 'content-box',
    },
    datePickerInput: {
        minWidth: 180,
        '& input': {
            minWidth: 180,
        },
    },
    tableCell: {
        padding: theme.spacing(1, 1.5),
    },
    tableCellHead: {
        fontWeight: 600,
        backgroundColor: theme.palette.action.hover,
    },
    addRowButton: {
        border: `1px dashed ${theme.palette.divider}`,
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.action.hover,
        },
    },
    propertiesSection: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.default,
    },
    propertiesHeader: {
        padding: theme.spacing(1.5, 2),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.action.hover,
    },
    propertiesList: {
        maxHeight: 360,
        overflowY: 'auto',
    },
    propertyRow: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1.25, 2),
        cursor: 'pointer',
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': { borderBottom: 'none' },
        '&:hover': { backgroundColor: theme.palette.action.hover },
    },
    footer: {
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        backgroundColor: theme.palette.background.paper,
    },
    footerActions: {
        display: 'flex',
        gap: theme.spacing(1.5),
    },
    cancelButton: {
        textTransform: 'none',
    },
    createButton: {
        textTransform: 'none',
        minWidth: 120,
    },
    hiddenInput: {
        display: 'none',
    },
}))

const defaultRow = (): BulkDeadlineRow => ({
    id: `row-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    date: '',
    title: '',
    description: '',
    reminderDate: '',
})

interface Props {
    open: boolean
    onClose: () => void
    onCreateBulk: (request: CreateBulkDeadlinesRequest) => Promise<unknown>
    bulkLoading?: boolean
    theme?: Theme
}

export const BulkDeadlineModal = (props: Props) => {
    const { open, onClose, onCreateBulk, bulkLoading = false } = props
    const theme = useTheme()
    const classes = useStyles()

    const [rows, setRows] = useState<BulkDeadlineRow[]>(() => [defaultRow()])
    const { rootUser, workspaceUser } = useUser()
    const [
        selectedPropertyState,
        setSelectedPropertyState,
    ] = useState<IdBoolMap>({})

    useEffect(() => {
        if (open) {
            setRows([defaultRow()])
            setSelectedPropertyState({})
        }
    }, [open])

    const filteredWorkspaces =
        rootUser?.workspaces.filter((wsOption) => {
            return (
                wsOption.active_workspace.organization ===
                    workspaceUser?.active_workspace.organization &&
                wsOption.active_workspace.company_type === APARTMENT
            )
        }) ?? []

    const selectedIds = Object.keys(selectedPropertyState).reduce<number[]>(
        (prev, strKey) => {
            const key = Number(strKey)
            if (selectedPropertyState[key]) return prev.concat(key)
            return prev
        },
        [],
    )
    const selectedCount = selectedIds.length

    const setCurrentWorkspaceSelection = () => {
        if (workspaceUser) {
            setSelectedPropertyState({
                [workspaceUser.active_workspace.id]: true,
            })
        }
    }

    const addRow = () => {
        setRows((prev) => [...prev, defaultRow()])
    }

    const removeRow = (id: string) => {
        setRows((prev) => {
            const next = prev.filter((r) => r.id !== id)
            return next.length ? next : [defaultRow()]
        })
    }

    const updateRow = (
        id: string,
        field: keyof BulkDeadlineRow,
        value: string,
    ) => {
        if (field === 'id') return
        setRows((prev) =>
            prev.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
        )
    }

    const handleDownloadExample = () => {
        const blob = new Blob([EXAMPLE_CSV], {
            type: 'text/csv;charset=utf-8;',
        })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = 'deadlines-example.csv'
        link.click()
        URL.revokeObjectURL(link.href)
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        const reader = new FileReader()
        reader.onload = () => {
            const text = String(reader.result ?? '')
            const parsed = parseCSV(text)
            if (parsed.length > 0) {
                setRows(
                    parsed.map((r) => ({
                        ...r,
                        id:
                            r.id ||
                            `row-${Date.now()}-${Math.random()
                                .toString(36)
                                .slice(2)}`,
                    })),
                )
            }
        }
        reader.readAsText(file)
        e.target.value = ''
    }

    const validRowCount = rows.filter((r) => r.title.trim() !== '').length
    const canCreate = selectedCount > 0 && validRowCount > 0

    return (
        <Modal
            open={open}
            onClose={onClose}
            className={classes.modalBackdrop}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper className={classes.paper}>
                    <Box className={classes.header}>
                        <EventIcon className={classes.headerIcon} />
                        <Typography
                            className={classes.headerTitle}
                            color="textPrimary"
                        >
                            Bulk add deadlines
                        </Typography>
                    </Box>

                    <Box className={classes.body}>
                        <Box>
                            <Typography className={classes.sectionLabel}>
                                Deadlines (date, title, description, reminder
                                date)
                            </Typography>
                            <Box className={classes.csvActions}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<GetAppIcon />}
                                    onClick={handleDownloadExample}
                                    style={{ textTransform: 'none' }}
                                >
                                    Download example CSV
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    component="label"
                                    startIcon={<PublishIcon />}
                                    style={{ textTransform: 'none' }}
                                >
                                    Upload CSV
                                    <input
                                        type="file"
                                        accept=".csv,text/csv"
                                        className={classes.hiddenInput}
                                        onChange={handleFileChange}
                                    />
                                </Button>
                            </Box>
                            <TableContainer
                                className={classes.tableWrap}
                                style={{ marginTop: theme.spacing(1.5) }}
                            >
                                <Table
                                    size="small"
                                    className={classes.table}
                                    stickyHeader
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                className={`${classes.tableCell} ${classes.tableCellHead} ${classes.dateCell}`}
                                            >
                                                Date
                                            </TableCell>
                                            <TableCell
                                                className={`${classes.tableCell} ${classes.tableCellHead}`}
                                            >
                                                Title
                                            </TableCell>
                                            <TableCell
                                                className={`${classes.tableCell} ${classes.tableCellHead}`}
                                            >
                                                Description
                                            </TableCell>
                                            <TableCell
                                                className={`${classes.tableCell} ${classes.tableCellHead} ${classes.dateCell}`}
                                            >
                                                Reminder
                                            </TableCell>
                                            <TableCell
                                                className={`${classes.tableCell} ${classes.tableCellHead}`}
                                                style={{ width: 56 }}
                                            />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((row) => (
                                            <TableRow key={row.id}>
                                                <TableCell
                                                    className={`${classes.tableCell} ${classes.dateCell}`}
                                                >
                                                    <KeyboardDatePicker
                                                        format="yyyy-MM-dd"
                                                        value={
                                                            row.date
                                                                ? (() => {
                                                                      const dt = DateTime.fromISO(
                                                                          row.date,
                                                                      )
                                                                      return dt.isValid
                                                                          ? dt.toJSDate()
                                                                          : null
                                                                  })()
                                                                : null
                                                        }
                                                        onChange={(d) => {
                                                            const v = d
                                                                ? DateTime.fromJSDate(
                                                                      d,
                                                                  ).toISODate() ??
                                                                  ''
                                                                : ''
                                                            updateRow(
                                                                row.id,
                                                                'date',
                                                                v,
                                                            )
                                                        }}
                                                        inputVariant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        placeholder="YYYY-MM-DD"
                                                        InputProps={{
                                                            className:
                                                                classes.datePickerInput,
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    className={
                                                        classes.tableCell
                                                    }
                                                >
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        variant="outlined"
                                                        value={row.title}
                                                        onChange={(e) =>
                                                            updateRow(
                                                                row.id,
                                                                'title',
                                                                e.target.value,
                                                            )
                                                        }
                                                        placeholder="e.g. Lease renewal"
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    className={
                                                        classes.tableCell
                                                    }
                                                >
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        variant="outlined"
                                                        value={row.description}
                                                        onChange={(e) =>
                                                            updateRow(
                                                                row.id,
                                                                'description',
                                                                e.target.value,
                                                            )
                                                        }
                                                        placeholder="Optional notes"
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    className={`${classes.tableCell} ${classes.dateCell}`}
                                                >
                                                    <KeyboardDatePicker
                                                        format="yyyy-MM-dd"
                                                        value={
                                                            row.reminderDate
                                                                ? (() => {
                                                                      const dt = DateTime.fromISO(
                                                                          row.reminderDate,
                                                                      )
                                                                      return dt.isValid
                                                                          ? dt.toJSDate()
                                                                          : null
                                                                  })()
                                                                : null
                                                        }
                                                        onChange={(d) => {
                                                            const v = d
                                                                ? DateTime.fromJSDate(
                                                                      d,
                                                                  ).toISODate() ??
                                                                  ''
                                                                : ''
                                                            updateRow(
                                                                row.id,
                                                                'reminderDate',
                                                                v,
                                                            )
                                                        }}
                                                        inputVariant="outlined"
                                                        size="small"
                                                        fullWidth
                                                        clearable
                                                        placeholder="Optional"
                                                        InputProps={{
                                                            className:
                                                                classes.datePickerInput,
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell
                                                    className={
                                                        classes.tableCell
                                                    }
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            removeRow(row.id)
                                                        }
                                                        aria-label="Remove row"
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell
                                                colSpan={5}
                                                className={classes.tableCell}
                                                style={{
                                                    borderBottom: 'none',
                                                    paddingTop: theme.spacing(
                                                        1,
                                                    ),
                                                    paddingBottom: theme.spacing(
                                                        0.5,
                                                    ),
                                                }}
                                            >
                                                <IconButton
                                                    size="small"
                                                    onClick={addRow}
                                                    aria-label="Add row"
                                                    className={
                                                        classes.addRowButton
                                                    }
                                                >
                                                    <AddIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>

                        <Box>
                            <Typography className={classes.sectionLabel}>
                                Apply to properties
                            </Typography>
                            <Box className={classes.propertiesSection}>
                                <Box className={classes.propertiesHeader}>
                                    <Checkbox
                                        checked={
                                            selectedCount ===
                                            filteredWorkspaces.length
                                        }
                                        indeterminate={
                                            selectedCount > 0 &&
                                            selectedCount <
                                                filteredWorkspaces.length
                                        }
                                        onChange={() => {
                                            if (selectedCount > 0) {
                                                setSelectedPropertyState({})
                                            } else {
                                                const newState: IdBoolMap = {}
                                                filteredWorkspaces.forEach(
                                                    (ws) => {
                                                        newState[
                                                            ws.active_workspace.id
                                                        ] = true
                                                    },
                                                )
                                                setSelectedPropertyState(
                                                    newState,
                                                )
                                            }
                                        }}
                                        color="primary"
                                    />
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {selectedCount === 0
                                            ? 'Select at least one property'
                                            : `${selectedCount} selected`}
                                    </Typography>
                                </Box>
                                <Box className={classes.propertiesList}>
                                    {filteredWorkspaces.map((ws) => {
                                        const propertyId =
                                            ws.active_workspace.id
                                        const isChecked =
                                            selectedPropertyState[
                                                propertyId
                                            ] === true
                                        return (
                                            <Box
                                                key={`WS-${propertyId}`}
                                                className={classes.propertyRow}
                                                onClick={() => {
                                                    setSelectedPropertyState({
                                                        ...selectedPropertyState,
                                                        [propertyId]: !isChecked,
                                                    })
                                                }}
                                            >
                                                <Checkbox
                                                    checked={isChecked}
                                                    color="primary"
                                                    size="small"
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                    onChange={() => {
                                                        setSelectedPropertyState(
                                                            {
                                                                ...selectedPropertyState,
                                                                [propertyId]: !isChecked,
                                                            },
                                                        )
                                                    }}
                                                />
                                                <Typography variant="body2">
                                                    {ws.active_workspace.name}
                                                </Typography>
                                            </Box>
                                        )
                                    })}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box className={classes.footer}>
                        <Typography variant="body2" color="textSecondary">
                            {validRowCount} deadline
                            {validRowCount !== 1 ? 's' : ''} with title
                        </Typography>
                        <Box className={classes.footerActions}>
                            <Button
                                variant="outlined"
                                onClick={onClose}
                                className={classes.cancelButton}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                className={classes.createButton}
                                disabled={!canCreate || bulkLoading}
                                onClick={() => {
                                    const deadlines = rows
                                        .filter((r) => r.title.trim() !== '')
                                        .map((r) => ({
                                            date: r.date,
                                            name: r.title.trim(),
                                            description:
                                                r.description?.trim() ?? '',
                                            reminder_date:
                                                r.reminderDate?.trim() || null,
                                        }))
                                    onCreateBulk({
                                        deadlines,
                                        apartments: selectedIds,
                                    }).catch(() => {
                                        toast.error(
                                            'Failed to create deadlines',
                                        )
                                    })
                                }}
                            >
                                {bulkLoading
                                    ? 'Creating…'
                                    : `Create${
                                          selectedCount > 0 && validRowCount > 0
                                              ? ` (${validRowCount})`
                                              : ''
                                      }`}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Slide>
        </Modal>
    )
}
